'use client';
import { useState, useCallback } from 'react';
import { Presentation, Download, RefreshCw, FileText, Layers, Image, Type, Sparkles } from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';
import { AgentPageLayout, AgentInputPanel, AgentLabel, AgentStreamOutput, AgentEmptyState } from '@/components/AgentPageLayout';
import { AgentToolbar } from '@/components/AgentToolbar';
import { AGENT_CONFIGS, downloadAsTextFile } from '@/lib/agent-constants';
import { NasProjectSelector, AgentModeToggle, useNasAnalysis } from '@/components/NasProjectSelector';

interface Slide { id: number; title: string; type: 'cover' | 'content' | 'table' | 'chart' | 'image' | 'summary'; content: string; speakerNotes?: string; }

const PPT_PROJECT_TYPES = ['BTL', 'PF', '관급', '민간', '리모델링', '인테리어'];

export default function PPTAgentPage() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const config = AGENT_CONFIGS.ppt;
    const [slides, setSlides] = useState<Slide[]>([]);
    const [selectedSlide, setSelectedSlide] = useState<Slide | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [aiStream, setAiStream] = useState('');
    const [aiDone, setAiDone] = useState(false);
    const [projectType, setProjectType] = useState('BTL');
    const [projectName, setProjectName] = useState('');
    const [projectDesc, setProjectDesc] = useState('');
    const [mode, setMode] = useState<'manual' | 'nas'>('nas');
    const nasAnalysis = useNasAnalysis();

    const handleNasAnalyze = useCallback(async (projectId: string, selectedFiles: string[]) => {
        setSlides([]);
        const fullText = await nasAnalysis.analyze(projectId, selectedFiles, 'ppt', `사업 유형: ${projectType}, 프로젝트명: ${projectName || projectId}`);
        if (fullText) {
            try {
                const jsonMatch = fullText.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    const parsed = JSON.parse(jsonMatch[0]);
                    if (parsed.slides && Array.isArray(parsed.slides)) {
                        const parsedSlides = parsed.slides.map((s: any, idx: number) => ({ id: s.id || idx + 1, title: s.title || '', type: s.type || 'content', content: s.content || '', speakerNotes: s.speakerNotes || '' }));
                        setSlides(parsedSlides);
                        setSelectedSlide(parsedSlides[0]);
                    }
                }
            } catch { }
        }
    }, [nasAnalysis, projectType, projectName]);

    const generatePPT = useCallback(async () => {
        setIsProcessing(true);
        setAiStream('');
        setAiDone(false);
        setSlides([]);

        try {
            const res = await fetch('/api/ppt-agent', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    projectType,
                    projectName: projectName || `${projectType} 사업제안서`,
                    description: projectDesc || `${projectType} 건축 사업제안서를 작성해주세요. 10장 내외의 슬라이드로 구성해주세요.`,
                    slideCount: 10
                }),
            });

            if (!res.ok) throw new Error('API 오류');
            const reader = res.body?.getReader();
            if (!reader) throw new Error('스트림 없음');
            const decoder = new TextDecoder();
            let fullText = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                for (const line of chunk.split('\n')) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.substring(6));
                            if (data.type === 'chunk') { fullText += data.text; setAiStream(fullText); }
                            else if (data.type === 'complete') { fullText = data.fullText; setAiStream(fullText); }
                        } catch { /* skip */ }
                    }
                }
            }

            try {
                const jsonMatch = fullText.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    const parsed = JSON.parse(jsonMatch[0]);
                    if (parsed.slides && Array.isArray(parsed.slides)) {
                        const parsedSlides = parsed.slides.map((s: any, idx: number) => ({
                            id: s.id || idx + 1,
                            title: s.title || `슬라이드 ${idx + 1}`,
                            type: s.type || 'content',
                            content: s.content || s.preview || '',
                            speakerNotes: s.speakerNotes || ''
                        }));
                        setSlides(parsedSlides);
                        setSelectedSlide(parsedSlides[0]);
                    }
                }
            } catch { /* raw text */ }

            setAiDone(true);
        } catch (err: any) {
            setAiStream(`오류: ${err.message}`);
            setAiDone(true);
        }
        setIsProcessing(false);
    }, [projectType, projectName, projectDesc]);

    const handleExport = () => {
        if (!aiStream) return;
        const filename = `PPT_슬라이드_${projectName || projectType}_${new Date().toISOString().slice(0,10)}.txt`;
        downloadAsTextFile(aiStream, filename);
    };

    const typeIcon = (t: string) => {
        if (t === 'cover') return <FileText className="w-3 h-3 text-blue-400" />;
        if (t === 'table') return <Layers className="w-3 h-3 text-emerald-400" />;
        if (t === 'image') return <Image className="w-3 h-3 text-purple-400" />;
        if (t === 'chart') return <Layers className="w-3 h-3 text-orange-400" />;
        if (t === 'summary') return <Type className="w-3 h-3 text-amber-400" />;
        return <FileText className="w-3 h-3 text-gray-400" />;
    };

    return (
        <AgentPageLayout config={config} icon={<Presentation className="w-5 h-5" />} isProcessing={isProcessing} aiDone={aiDone}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* Input Panel */}
                <div className="lg:col-span-3">
                    <AgentInputPanel>
                        <AgentModeToggle mode={mode} onModeChange={setMode} accentBg={config.accentBg} accentText={config.accentText} accentBorder={config.accentBorder} />
                        {mode === 'nas' ? (
                            <NasProjectSelector agentType="ppt" accentBg={config.accentBg} accentText={config.accentText} accentBorder={config.accentBorder} gradient={config.gradient} onAnalyze={handleNasAnalyze} isProcessing={nasAnalysis.isProcessing}>
                                <div>
                                    <AgentLabel>사업 유형</AgentLabel>
                                    <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                                        {PPT_PROJECT_TYPES.map(t => (
                                            <button key={t} onClick={() => setProjectType(t)}
                                                className={`py-1.5 rounded-lg text-[10px] font-bold cursor-pointer transition ${projectType === t ? `${config.accentBg} ${config.accentText} border ${config.accentBorder}` : isDark ? 'bg-white/5 text-gray-500 border border-white/10' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>{t}</button>
                                        ))}
                                    </div>
                                </div>
                            </NasProjectSelector>
                        ) : (
                            <>
                                <div>
                                    <AgentLabel>사업 유형</AgentLabel>
                                    <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                                        {PPT_PROJECT_TYPES.map(t => (
                                            <button key={t} onClick={() => setProjectType(t)}
                                                className={`py-2 rounded-lg text-[10px] font-bold cursor-pointer transition ${projectType === t ? `${config.accentBg} ${config.accentText} border ${config.accentBorder}` : isDark ? 'bg-white/5 text-gray-500 border border-white/10' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>{t}</button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <AgentLabel>프로젝트명</AgentLabel>
                                    <input value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="예: 웅촌초 BTL 사업"
                                        className={`w-full mt-1.5 px-3 py-2.5 rounded-lg text-xs ${isDark ? 'bg-white/5 border border-white/10 text-white placeholder-gray-600' : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400'}`} />
                                </div>
                                <div>
                                    <AgentLabel>추가 지시사항</AgentLabel>
                                    <textarea value={projectDesc} onChange={e => setProjectDesc(e.target.value)} placeholder="특이사항, 강조 내용 등" rows={3}
                                        className={`w-full mt-1.5 px-3 py-2.5 rounded-lg text-xs resize-none ${isDark ? 'bg-white/5 border border-white/10 text-white placeholder-gray-600' : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400'}`} />
                                </div>
                                <button onClick={generatePPT} disabled={isProcessing}
                                    className={`w-full py-3 rounded-xl bg-gradient-to-r ${config.gradient} text-white text-xs font-bold cursor-pointer disabled:opacity-40 flex items-center justify-center gap-2 shadow-lg`}>
                                    {isProcessing ? <><RefreshCw className="w-4 h-4 animate-spin" />AI 생성 중...</> : <><Sparkles className="w-4 h-4" />AI PPT 자동 생성</>}
                                </button>
                            </>
                        )}
                    </AgentInputPanel>
                </div>

                {/* Slides Panel */}
                <div className="lg:col-span-9">
                    {slides.length > 0 ? (
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                            {/* Slide List */}
                            <div className="lg:col-span-3">
                                <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                                    <div className={`px-4 py-2.5 border-b text-xs font-bold flex items-center justify-between ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'}`}>
                                        슬라이드 ({slides.length})
                                        <button onClick={handleExport} className={`text-[10px] px-2 py-1 rounded-lg cursor-pointer flex items-center gap-1 font-bold transition hover:opacity-80 ${isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-50 text-blue-600'}`}><Download className="w-3 h-3" />내보내기</button>
                                    </div>
                                    <div className="p-2 space-y-1 max-h-[500px] overflow-y-auto">
                                        {slides.map(s => (
                                            <button key={s.id} onClick={() => setSelectedSlide(s)}
                                                className={`w-full text-left px-3 py-2.5 rounded-xl text-[11px] transition cursor-pointer flex items-center gap-2 ${
                                                    selectedSlide?.id === s.id
                                                        ? isDark ? `${config.accentBg} border ${config.accentBorder}` : 'bg-blue-50 border border-blue-300'
                                                        : isDark ? 'hover:bg-white/5 border border-transparent' : 'hover:bg-gray-50 border border-transparent'
                                                }`}>
                                                <span className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold shrink-0 ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>{s.id}</span>
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-semibold truncate flex items-center gap-1">{typeIcon(s.type)} {s.title}</div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            {/* Slide Preview */}
                            <div className="lg:col-span-9">
                                {selectedSlide && (
                                    <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                                        <div className={`px-4 py-2.5 border-b text-xs font-bold ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'}`}>
                                            슬라이드 {selectedSlide.id} — {selectedSlide.title}
                                        </div>
                                        <div className="aspect-[16/9] flex items-center justify-center p-8" style={{ background: isDark ? 'linear-gradient(135deg, #0f172a, #1e293b)' : 'linear-gradient(135deg, #f8fafc, #e2e8f0)' }}>
                                            <div className="text-center max-w-2xl">
                                                {selectedSlide.type === 'cover' ? (
                                                    <>
                                                        <div className={`text-3xl font-bold mb-4 bg-gradient-to-r ${config.gradient} bg-clip-text text-transparent`}>{selectedSlide.title}</div>
                                                        <div className={`text-sm leading-relaxed whitespace-pre-wrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{selectedSlide.content}</div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="text-xl font-bold mb-4">{selectedSlide.title}</div>
                                                        <div className={`text-sm leading-relaxed text-left whitespace-pre-wrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{selectedSlide.content}</div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        {selectedSlide.speakerNotes && (
                                            <div className={`px-4 py-3 border-t text-[10px] ${isDark ? 'border-white/10 bg-white/[0.02] text-gray-500' : 'border-gray-100 bg-gray-50 text-gray-400'}`}>
                                                <span className="font-bold">발표자 노트: </span>{selectedSlide.speakerNotes}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <>
                            <AgentStreamOutput aiStream={aiStream} isProcessing={isProcessing} accentColor="text-blue-400" />
                            {aiStream && !isProcessing && (
                                <AgentToolbar title="PPT 생성" content={aiStream} agentType="ppt" />
                            )}

                            {!aiStream && !isProcessing && (
                                <AgentEmptyState
                                    icon={<Presentation className={`w-8 h-8 ${isDark ? 'text-blue-500/30' : 'text-blue-200'}`} />}
                                    message="프로젝트 정보를 입력하고 AI PPT 생성을 시작하세요"
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </AgentPageLayout>
    );
}
